//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@banno/jha-wc/src/forms/jha-form-input-base/jha-form-input";
import "@banno/jha-wc/src/forms/jha-form-select/jha-form-select";
import "@banno/jha-wc/src/forms/jha-form-switch/jha-form-switch";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/tables/jha-table/jha-table";
import "@banno/jha-wc/src/tables/jha-table-row/jha-table-row";
import "@banno/jha-wc/src/tables/jha-table-header/jha-table-header";
import "@banno/jha-wc/src/tables/jha-table-cell/jha-table-cell";
import "@banno/jha-wc/src/icons/jha-icon-plus";
import "@banno/jha-wc/src/icons/jha-icon-edit";
import "@banno/jha-wc/src/icons/jha-icon-delete";
import "@banno/jha-wc/src/icons/jha-icon-close";
import "@banno/jha-wc/src/icons/jha-icon-circle-checkmark-filled";
import "@banno/jha-wc/src/icons/jha-icon-warning-filled";
import "@banno/jha-wc/src/jha-tooltip/jha-tooltip";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import Cookies from "js-cookie";
import { WorkflowTemplateGeneralViewMode } from "../../types/WorkflowTemplates/WorkflowTemplateTypeIndex";
import { WorkflowTemplateVariable } from "../../types/WorkflowTemplates/WorkflowTemplateVariable";
import { getRequest, putRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import {
  decryptValue,
  getEnvApiUrl,
  isBusinessUnitAdminUserType,
  isEnterpriseAdminUserType,
  showToastMessage,
  stringComparison,
} from "../../utils/app-utils";
import {
  isBuiltinBusUnitVariableName,
  isBuiltinRegionVariableName,
} from "../../utils/workflowTemplatePlaceholderSyntax";
/**
 * Workflow Template Settings Tab
 *
 * This tab contains:
 * - Basic template information (Name, Description, Category, Business Unit)
 * - Status toggle (Active/Inactive) - positioned between Basic Info and Variables
 * - Template Variables management (table with add/edit/delete)
 *
 * Validation:
 * - Template Name: required, alphanumeric with dots, dashes, underscores
 * - Template Description: required
 * - Category: required ("global" or "businessunit")
 * - Business Unit: required only when Category is "businessunit"
 * - Must have at least one variable for template to be valid (optional initially)
 *
 * Variable fields match C# API (TemplateVariable.cs):
 * - Name: Variable identifier
 * - Description: Human-readable description
 * - RegexRule: Optional validation pattern
 * - RuleDescription: Description shown when validation fails
 */
@customElement("workflow-template-settings-tab")
export class WorkflowTemplateSettingsTab extends formElementMixin(LitElement) {
  static get is() {
    return "workflow-template-settings-tab";
  }

  // Mode and Permission Properties
  @property({ type: String }) mode: WorkflowTemplateGeneralViewMode = "ADD";
  @property({ type: String }) workflowTemplateId: string = "";
  @property({ type: Boolean }) isReadOnly: boolean = false;
  @property({ type: Object }) templateData: any = null; // Passed from parent - no need to fetch

  // Variables in use - passed from parent to prevent deletion of referenced variables
  @property({ type: Array }) variablesInUse: string[] = [];

  // User Information (refreshed from cookies in connectedCallback)
  @property({ type: String }) loggedUserType = "";
  @property({ type: String }) loggedUserBu = "";
  @property({ type: Boolean }) isEnterpriseAdmin = false;
  @property({ type: Boolean }) isBusinessUnitAdmin = false;

  private refreshRoleFromCookies(): void {
    this.loggedUserType = decryptValue(Cookies.get("userType") || "");
    this.loggedUserBu = Cookies.get("businessUnit") || "";
    const userType = this.loggedUserType;
    this.isEnterpriseAdmin = isEnterpriseAdminUserType(userType);
    this.isBusinessUnitAdmin = isBusinessUnitAdminUserType(userType);
  }

  /** ADD flow: BU admin creates only BU-scoped templates (not enterprise-wide). */
  private get isBuAdminAddMode(): boolean {
    return (
      this.mode === "ADD" && this.isBusinessUnitAdmin && !this.isEnterpriseAdmin
    );
  }

  // Form Data
  @state() templateName: string = "";
  @state() templateDescription: string = "";
  @state() visibilityLevel: string = "";
  @state() businessUnit: string = "";
  @state() templateStatus: string = "Inactive"; // Active or Inactive

  // Variables - stored as array, position determines display order
  @state() variables: WorkflowTemplateVariable[] = [];

  // Business Units List
  @state() businessUnits: Array<{ value: string; name: string }> = [];

  // Validation States
  @state() isTemplateNameValid: boolean = false;
  @state() isTemplateDescriptionValid: boolean = false;
  @state() isCategoryValid: boolean = false;
  @state() isBusinessUnitValid: boolean = true; // True by default (not always required)

  // Loading State
  @state() apiLoading: boolean = false;

  // Dirty State Tracking - indicates unsaved changes
  @state() isDirty: boolean = false;

  // Track invalid regex patterns per variable index (for real-time validation)
  @state() private regexErrors: Map<number, boolean> = new Map();
  // Track custom error messages per variable index
  @state() private regexErrorMessages: Map<number, string> = new Map();

  // Flag to prevent marking dirty during initial load/render
  private isInitializing: boolean = true;

  // Original values for dirty detection
  private originalData = {
    templateName: "",
    templateDescription: "",
    visibilityLevel: "",
    businessUnit: "",
    templateStatus: "Inactive",
    variablesJson: "[]", // JSON string for comparison
  };

  static readonly styles = css`
    :host {
      display: block;
      padding: 20px;
    }

    h2 {
      margin-top: 0;
      margin-bottom: 24px;
      font-size: 24px;
      font-weight: 600;
    }

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 400;
    }

    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
      h3 {
        color: #a3d4ff;
      }
    }

    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
      h3 {
        color: #06185f;
      }
    }

    #sectionHeader {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }

    #sectionHeader h2 {
      margin: 0;
    }

    #basicInfoContainer {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 24px;
      margin-bottom: 32px;
    }

    #basicInfoContainer > * {
      min-width: 0; /* Prevent grid blowout */
    }

    /* Force 4 columns on large screens */
    @media (min-width: 1200px) {
      #basicInfoContainer {
        grid-template-columns: repeat(4, 1fr);
      }
    }

    /* Force 2 columns on medium screens */
    @media (min-width: 768px) and (max-width: 1199px) {
      #basicInfoContainer {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    /* Single column on small screens */
    @media (max-width: 767px) {
      #basicInfoContainer {
        grid-template-columns: 1fr;
      }
    }

    #statusContainer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 12px;
    }

    #statusContainer label {
      font-weight: 600;
      font-size: 16px;
    }

    #variablesSection {
      margin-top: 32px;
      display: flex;
      flex-direction: column;
    }

    .variables-section-heading {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      margin-bottom: 16px;
    }

    .variables-section-heading h2 {
      margin: 0;
    }

    .variables-section-hint {
      display: block;
      font-size: 12px;
      line-height: 1.35;
      font-weight: 400;
      color: var(--jha-color-secondary, #6c757d);
    }

    #variablesTableContainer {
      display: block;
      border: 1px solid var(--jha-color-border, #e0e0e0);
      border-radius: 4px;
      overflow: visible;
    }

    #variablesTableContainer jha-table {
      width: 100%;
      table-layout: fixed;
    }

    #variablesTableContainer jha-table-cell {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Allow overflow for actions column so tooltips can display */
    #variablesTableContainer jha-table-cell:last-child {
      overflow: visible;
    }

    /* Ensure tooltips in the table appear above other rows */
    #variablesTableContainer jha-tooltip {
      --jha-tooltip-z-index: 9999;
    }

    #variablesTableContainer .action-icons {
      position: relative;
      z-index: 1;
    }

    #addVariableButtonContainer {
      display: flex;
      justify-content: flex-end;
      margin-top: 16px;
    }

    .variables-warning {
      color: var(--jha-color-danger, #c9252d);
      font-size: 14px;
      margin: 0 0 15px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .variables-warning jha-icon-warning-filled {
      flex-shrink: 0;
      --jha-icon-color: var(--jha-color-danger, #c9252d);
    }

    jha-table {
      width: 100%;
      border-collapse: collapse;
    }

    jha-table-header {
      font-weight: 600;
      padding: 12px;
      text-align: left;
      background: var(--jh-color-container-primary-enabled, rgb(244, 244, 244));
      border-top: 1px solid var(--jh-color-divider-primary);
      border-left: 1px solid var(--jha-advanced-table-cell-border);
    }

    jha-table-cell {
      padding: 12px;
      border-left: 1px solid var(--jha-advanced-table-cell-border);
      vertical-align: middle;
      font-size: 15px;
      line-height: 1.4;
    }

    .no-variables-message {
      padding: 32px;
      text-align: center;
      border: 1px solid;
      border-top: none;
    }

    jha-table-row {
      border-bottom: 1px solid;
    }

    #variablesTableContainer jha-table-row {
      border-bottom: 1px solid;
      overflow: visible;
    }

    /* Light mode - matches jha-advanced-table default styling */
    @media (prefers-color-scheme: light) {
      .no-variables-message {
        color: #666666;
        border-color: #e0e0e0;
      }

      jha-table-row {
        border-bottom-color: #e0e0e0;
      }

      #variablesTableContainer {
        border-color: #e0e0e0;
      }

      #variablesTableContainer jha-table-row {
        border-bottom-color: #e0e0e0;
      }

      .inline-input {
        background-color: #ffffff;
        color: #000000;
        border-color: #d0d0d0;
      }

      .inline-input:focus {
        border-color: #0066cc;
        border-width: 1px;
      }
    }

    /* Dark mode - matches jha-advanced-table dark mode styling */
    @media (prefers-color-scheme: dark) {
      .no-variables-message {
        color: #a0a0a0;
        border-color: #3a3a3a;
      }

      jha-table {
        border: 1px solid #3a3a3a;
      }

      jha-table-row {
        border-bottom-color: #3a3a3a;
      }

      #variablesTableContainer {
        border-color: #3a3a3a;
      }

      #variablesTableContainer jha-table-row {
        border-bottom-color: #3a3a3a;
      }

      .inline-input {
        background-color: #2a2a2a;
        color: #e0e0e0;
        border-color: #444444;
      }

      .inline-input:focus {
        border-color: #4da6ff;
        border-width: 1px;
      }

      jha-icon-edit {
        color: #4da6ff;
      }

      jha-icon-circle-checkmark-filled {
        color: #28a745;
      }

      jha-icon-delete {
        color: #ff6b6b;
      }

      jha-icon-close {
        color: #999999;
      }
    }

    jha-form-input,
    jha-form-select {
      width: 100%;
    }

    /* Inline editing styles - border colors match jh-input/template-string-input */
    .inline-input {
      width: 100%;
      padding: 10px;
      border: 1px solid #cccccc;
      border-radius: 4px;
      font-family: inherit;
      font-size: 15px;
      box-sizing: border-box;
      background-color: transparent;
      color: inherit;
    }

    @media (prefers-color-scheme: dark) {
      .inline-input {
        border-color: #444444;
        background-color: transparent;
      }
    }

    .inline-input:focus {
      outline: none;
      border-color: var(--jha-color-primary, #0066cc);
    }

    .inline-input.invalid {
      border-color: var(--jha-color-warning, #f5a623);
      background-color: rgba(245, 166, 35, 0.05);
    }

    .inline-input.invalid:focus {
      border-color: var(--jha-color-warning, #f5a623);
    }

    .inline-input.readonly,
    .inline-input:disabled {
      background-color: #f5f5f5;
      color: #666666;
      cursor: not-allowed;
      opacity: 0.7;
    }

    @media (prefers-color-scheme: dark) {
      .inline-input.readonly,
      .inline-input:disabled {
        background-color: #1a1a1a;
        color: #888888;
      }
    }

    .regex-error-message {
      color: var(--jha-color-warning, #f5a623);
      font-size: 11px;
      margin-top: 2px;
      display: block;
    }

    .inline-input::placeholder {
      color: #888888;
    }

    @media (prefers-color-scheme: dark) {
      .inline-input::placeholder {
        color: #666666;
      }

      .inline-input.invalid {
        background-color: rgba(245, 166, 35, 0.1);
      }
    }

    .action-icons {
      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: center;
      width: 100%;
    }

    /* Action cell - centered content */
    .action-cell {
      text-align: center;
    }

    .action-icon {
      cursor: pointer;
      width: 20px;
      height: 20px;
      color: var(--jha-color-primary, #0066cc);
      transition: opacity 0.2s;
    }

    .action-icon:hover {
      opacity: 0.7;
    }

    .action-icon[disabled],
    .action-icon.disabled {
      cursor: not-allowed;
      opacity: 0.3;
      pointer-events: none;
    }

    jha-icon-edit {
      cursor: pointer;
      color: var(--jha-color-primary, #0066cc);
    }

    jha-icon-delete {
      cursor: pointer;
      color: var(--jha-color-danger, #dc3545);
    }

    jha-icon-close {
      cursor: pointer;
      color: var(--jha-color-secondary, #6c757d);
    }

    jha-icon-circle-checkmark-filled {
      cursor: pointer;
      color: #28a745;
    }
  `;

  async connectedCallback() {
    this.refreshRoleFromCookies();
    // Set loading state BEFORE rendering
    this.apiLoading = true;

    // Load business units BEFORE rendering
    await this.loadBusinessUnits();

    // Load template data if in EDIT or VIEW mode
    if (this.workflowTemplateId) {
      await this.loadTemplateData();
    }

    // Set initial defaults for ADD mode
    if (this.mode === "ADD") {
      this.templateStatus = "Inactive";

      // ADD mode - new template starts clean (isDirty = false)
      // Dirty state is only set when user actually changes something via markDirty()
      // This ensures no "unsaved changes" warning if user cancels without making changes
      this.isDirty = false;

      // BU Admin (read/write): only Business Unit category; lock to user's BU (single option each, preselected)
      if (this.isBuAdminAddMode && this.loggedUserBu) {
        this.visibilityLevel = "Business Unit";
        this.isCategoryValid = true;
        const bu = this.businessUnits.find(
          (b) => b.value.toLowerCase() === this.loggedUserBu?.toLowerCase(),
        );
        this.businessUnit = bu ? bu.value : this.loggedUserBu;
        this.checkBusinessUnitValidity();
      } else {
        this.visibilityLevel = "";
      }

      // Baseline for dirty detection after defaults (especially BU admin preselection)
      this.saveOriginalData();
    }

    // Clear loading state
    this.apiLoading = false;

    // NOW render with all data ready
    super.connectedCallback();

    this.addEventListener("paste", this.onTemplateNamePasteCapture, true);

    this.validateForm();
  }

  disconnectedCallback() {
    this.removeEventListener("paste", this.onTemplateNamePasteCapture, true);
    this.templateNameInnerInput?.removeEventListener(
      "paste",
      this.onTemplateNameInnerPaste,
      true,
    );
    this.templateNameInnerInput = null;
    super.disconnectedCallback();
  }

  updated() {
    this.enforceTemplateNameMaxLength();
    this.enforceTemplateDescriptionMaxLength();
  }

  /**
   * Called after the first render - safe to start tracking dirty state
   * and emit initial validation to parent
   */
  firstUpdated() {
    this.enforceTemplateNameMaxLength();
    this.enforceTemplateDescriptionMaxLength();
    // Allow a brief delay for all @change events from initial render to complete
    setTimeout(() => {
      this.isInitializing = false;
      // Re-emit validation now that parent's event listeners are definitely attached
      this.validateForm();
    }, 100);
  }

  /**
   * Load business units from API
   */
  async loadBusinessUnits() {
    try {
      // getEnvApiUrl: localhost-only; .env.local never ships — no effect in prod, test, staging, or CI builds.
      const response = await getRequest(
        getEnvApiUrl(Apis.GET_ALL_BUSINESSUNITVALUES),
      );
      const businessUnitsRes = response?.businessUnitValues ?? [];

      // Match Workflow pattern exactly
      this.businessUnits = businessUnitsRes
        .filter((bu: any) => bu.status === "active")
        .map((bu: any) => ({ value: bu.value, name: bu.name }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error("Failed to load business units:", error);
      this.businessUnits = [];
    }
  }

  /**
   * Load template data for EDIT/VIEW mode
   * Uses templateData from parent if available (single fetch optimization)
   **/
  async loadTemplateData() {
    this.apiLoading = true;

    try {
      // Use templateData from parent if available, otherwise fetch (fallback for tests)
      const template =
        this.templateData ||
        (await getRequest(
          `${Apis.GET_WORKFLOW_TEMPLATE}/${this.workflowTemplateId}`,
        ));

      if (template && !template?.details?.length) {
        this.templateName = template.templateName || "";
        this.templateDescription = template.description || "";
        // Category is "global" or "businessunit" (lowercase from API)
        // Map to display values for dropdown
        this.visibilityLevel =
          template.category === "global" ? "Global" : "Business Unit";
        this.templateStatus = template.status || "inactive";

        // BusinessUnitId contains the businessunitid (e.g., "bu_014") - never the name
        this.businessUnit = template.businessUnitId || "";

        // Validate loaded data
        this.isTemplateNameValid = this.templateName.length > 0;
        this.isTemplateDescriptionValid = this.templateDescription.length > 0;
        this.isCategoryValid = this.visibilityLevel.length > 0;
        this.checkBusinessUnitValidity();

        // Variables are nested IN the template entity (C# API / DynamoDB pattern)
        if (template.variables && Array.isArray(template.variables)) {
          // Array order from DynamoDB is the display order - no sorting needed.
          // Hide synthetic built-in variables (merged on save / generate only): the S3 Remote
          // region placeholder and the always-injected Business Unit dropdown.
          this.variables = template.variables.filter(
            (v: any) =>
              !isBuiltinRegionVariableName(v.name || "") &&
              !isBuiltinBusUnitVariableName(v.name || ""),
          );
        } else {
          // No variables yet - initialize empty array
          this.variables = [];
        }
      }

      // Save original data for dirty detection after all data is loaded
      this.saveOriginalData();

      // Reset dirty state since we just loaded fresh data
      this.isDirty = false;
    } catch (error) {
      showToastMessage("Failed to load template data.", "danger");
    } finally {
      this.apiLoading = false;
      this.validateForm();
    }
  }

  /**
   * Validate template name
   */
  validateTemplateName(name: string) {
    const nameField = this.shadowRoot?.querySelector(
      "jha-form-input[data-auto-id='templateNameInput']",
    ) as any;

    const regex = /^[A-Za-z0-9._\-]+$/;

    if (!name || name.trim().length === 0) {
      if (nameField) {
        nameField.warning = "Template Name is required";
        nameField.showWarning = true;
      }
      this.isTemplateNameValid = false;
    } else if (!regex.test(name)) {
      if (nameField) {
        nameField.warning =
          "Template Name can only contain letters, numbers, dots, dashes, and underscores";
        nameField.showWarning = true;
      }
      this.isTemplateNameValid = false;
    } else {
      if (nameField) {
        nameField.showWarning = false;
      }
      this.isTemplateNameValid = true;
    }

    this.templateName = name;
    this.markDirty();
    this.validateForm();
  }

  // Valid characters for template name (a-zA-Z0-9._-)
  private static readonly VALID_TEMPLATE_NAME_CHARS = /^[a-zA-Z0-9._\-]$/;
  private static readonly TEMPLATE_NAME_MAX_LENGTH = 128;
  private static readonly TEMPLATE_DESCRIPTION_MAX_LENGTH = 512;

  private getTemplateNameFormInput(): HTMLElement | null {
    return (
      this.shadowRoot?.querySelector(
        "jha-form-input[data-auto-id='templateNameInput']",
      ) ?? null
    );
  }

  private getTemplateNameNativeInput(): HTMLInputElement | null {
    return (
      this.getTemplateNameFormInput()?.shadowRoot?.querySelector("input") ??
      null
    );
  }

  private templateNameInnerInput: HTMLInputElement | null = null;

  private readonly onTemplateNameInnerPaste = (e: ClipboardEvent) => {
    this.onTemplateNamePaste(e);
  };

  /** jha-form-input defaults maxlength to Infinity; force the inner native limit. */
  private enforceTemplateNameMaxLength() {
    const max = WorkflowTemplateSettingsTab.TEMPLATE_NAME_MAX_LENGTH;
    const formInput = this.getTemplateNameFormInput() as {
      maxlength?: number;
      updateComplete?: Promise<unknown>;
    } | null;
    if (formInput && formInput.maxlength !== max) {
      formInput.maxlength = max;
    }
    const applyToNative = () => {
      const native = this.getTemplateNameNativeInput();
      if (!native) {
        return;
      }
      native.maxLength = max;
      native.setAttribute("maxlength", String(max));
      if (this.templateNameInnerInput !== native) {
        this.templateNameInnerInput?.removeEventListener(
          "paste",
          this.onTemplateNameInnerPaste,
          true,
        );
        this.templateNameInnerInput = native;
        native.addEventListener("paste", this.onTemplateNameInnerPaste, true);
      }
    };
    applyToNative();
    if (formInput?.updateComplete) {
      void formInput.updateComplete.then(applyToNative);
    }
  }

  /** jha-form-input textarea also defaults maxlength to Infinity. */
  private enforceTemplateDescriptionMaxLength() {
    const max = WorkflowTemplateSettingsTab.TEMPLATE_DESCRIPTION_MAX_LENGTH;
    const formInput = this.shadowRoot?.querySelector(
      "jha-form-input[data-auto-id='templateDescriptionInput']",
    ) as {
      maxlength?: number;
      updateComplete?: Promise<unknown>;
      shadowRoot?: ShadowRoot;
    } | null;
    if (formInput && formInput.maxlength !== max) {
      formInput.maxlength = max;
    }
    const applyToNative = () => {
      const native = formInput?.shadowRoot?.querySelector(
        "input, textarea",
      ) as HTMLInputElement | HTMLTextAreaElement | null;
      if (!native) {
        return;
      }
      native.maxLength = max;
      native.setAttribute("maxlength", String(max));
    };
    applyToNative();
    if (formInput?.updateComplete) {
      void formInput.updateComplete.then(applyToNative);
    }
  }

  private readonly onTemplateNamePasteCapture = (e: ClipboardEvent) => {
    const path = typeof e.composedPath === "function" ? e.composedPath() : [];
    const onTemplateName = path.some(
      (n) =>
        n instanceof HTMLElement &&
        n.getAttribute("data-auto-id") === "templateNameInput",
    );
    if (!onTemplateName) {
      return;
    }
    this.onTemplateNamePaste(e);
  };

  /**
   * jha-form-input paste/input events retarget to the host. The visible value lives
   * on the inner native <input> in its shadow root.
   */
  private nativeTextInputFromEvent(e: Event): HTMLInputElement | null {
    const fromField = this.getTemplateNameNativeInput();
    if (fromField) {
      return fromField;
    }
    const path = typeof e.composedPath === "function" ? e.composedPath() : [];
    for (const node of path) {
      if (node instanceof HTMLInputElement) {
        return node;
      }
    }
    const host = (e.currentTarget ?? e.target) as HTMLElement | null;
    return host?.shadowRoot?.querySelector("input") ?? null;
  }

  /**
   * Handle keydown in Template Name field - block spaces and invalid characters.
   * Allowed: a-zA-Z0-9._-
   */
  onTemplateNameKeydown(e: KeyboardEvent) {
    // Allow control keys (backspace, delete, arrow keys, tab, etc.)
    if (
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "Tab" ||
      e.key === "Home" ||
      e.key === "End" ||
      e.key === "Enter" ||
      e.key === "Escape" ||
      e.ctrlKey ||
      e.metaKey
    ) {
      return; // Allow these keys
    }

    // Block space explicitly and any character not in the valid set
    if (
      e.key === " " ||
      !WorkflowTemplateSettingsTab.VALID_TEMPLATE_NAME_CHARS.test(e.key)
    ) {
      e.preventDefault();
    }
  }

  /**
   * Handle paste in Template Name field - sanitize pasted content.
   * Removes spaces and invalid characters.
   */
  onTemplateNamePaste(e: ClipboardEvent) {
    e.preventDefault();

    const pastedText = e.clipboardData?.getData("text") || "";
    const formInput =
      this.getTemplateNameFormInput() as HTMLInputElement | null;
    const native = this.nativeTextInputFromEvent(e);
    const fallback = (e.target as HTMLInputElement) || formInput;
    const input = native ?? fallback;
    const currentValue = input?.value || "";
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;

    // Sanitize pasted text: remove spaces and invalid chars, then cap at max length
    const sanitized = pastedText.replace(/[^a-zA-Z0-9._\-]/g, "");

    // Build new value
    const before = currentValue.slice(0, selectionStart);
    const after = currentValue.slice(selectionEnd);
    const newValue = (before + sanitized + after).slice(
      0,
      WorkflowTemplateSettingsTab.TEMPLATE_NAME_MAX_LENGTH,
    );

    if (native) {
      native.value = newValue;
    }
    if (formInput && formInput !== native) {
      formInput.value = newValue;
    }
    if (!native && fallback && fallback !== formInput) {
      fallback.value = newValue;
    }
    this.templateName = newValue;
    this.validateTemplateName(newValue);

    // Set cursor position after pasted content (may be shorter if truncated)
    const newCursorPos = Math.min(
      selectionStart + sanitized.length,
      newValue.length,
    );
    requestAnimationFrame(() => {
      native?.setSelectionRange(newCursorPos, newCursorPos);
    });
  }

  /**
   * Handle input in Template Name field - sanitize any invalid input including spaces.
   */
  onTemplateNameInput(e: Event) {
    const formInput =
      this.getTemplateNameFormInput() as HTMLInputElement | null;
    const native = this.nativeTextInputFromEvent(e);
    const fallback = (e.target as HTMLInputElement) || formInput;
    const value = (native ?? fallback)?.value || "";

    // Remove spaces and invalid characters, then cap at max length
    const sanitized = value
      .replace(/[^a-zA-Z0-9._\-]/g, "")
      .slice(0, WorkflowTemplateSettingsTab.TEMPLATE_NAME_MAX_LENGTH);

    if (native && native.value !== sanitized) {
      const cursorPos = native.selectionStart ?? sanitized.length;
      native.value = sanitized;
      requestAnimationFrame(() => {
        native.setSelectionRange(
          Math.min(cursorPos, sanitized.length),
          Math.min(cursorPos, sanitized.length),
        );
      });
    }
    if (formInput && formInput !== native && formInput.value !== sanitized) {
      formInput.value = sanitized;
    }
    if (
      !native &&
      fallback &&
      fallback !== formInput &&
      fallback.value !== sanitized
    ) {
      fallback.value = sanitized;
    }
    this.templateName = sanitized;

    // Validate immediately while typing for real-time feedback
    this.validateTemplateName(this.templateName);
    this.markDirty();
    this.validateForm();
  }

  /**
   * Validate template description
   */
  validateTemplateDescription(description: string) {
    const descField = this.shadowRoot?.querySelector(
      "jha-form-input[data-auto-id='templateDescriptionInput']",
    ) as any;

    if (!description || description.trim().length === 0) {
      if (descField) {
        descField.warning = "Template Description is required";
        descField.showWarning = true;
      }
      this.isTemplateDescriptionValid = false;
    } else {
      if (descField) {
        descField.showWarning = false;
      }
      this.isTemplateDescriptionValid = true;
    }

    this.templateDescription = description;
    this.markDirty();
    this.validateForm();
  }

  /**
   * Handle category change
   */
  onCategoryChange(e: Event) {
    const target = e.target as any;
    this.visibilityLevel = target.value;
    this.isCategoryValid = this.visibilityLevel.length > 0;

    // Reset business unit when switching to Global
    if (this.visibilityLevel === "Global") {
      this.businessUnit = "";
      this.isBusinessUnitValid = true;
    } else {
      // Business Unit is now required
      this.checkBusinessUnitValidity();
    }

    this.markDirty();
    this.validateForm();
  }

  /**
   * Handle business unit change
   */
  onBusinessUnitChange(e: Event) {
    const target = e.target as any;
    this.businessUnit = target.value;
    this.checkBusinessUnitValidity();
    this.markDirty();
    this.validateForm();
  }

  /**
   * Check business unit validity and show/hide warning
   */
  checkBusinessUnitValidity() {
    const buField = this.shadowRoot?.querySelector(
      "jha-form-select[data-auto-id='businessUnitSelect']",
    ) as any;

    if (this.visibilityLevel === "Business Unit") {
      this.isBusinessUnitValid = this.businessUnit.length > 0;

      // Show warning if business unit is required but empty
      if (buField) {
        if (!this.isBusinessUnitValid) {
          buField.warning =
            "Business Unit is required when Category is Business Unit";
          buField.showWarning = true;
        } else {
          buField.showWarning = false;
        }
      }
    } else {
      this.isBusinessUnitValid = true;
      // Hide warning when visibility is Global
      if (buField) {
        buField.showWarning = false;
      }
    }
  }

  /**
   * Handle status toggle
   * Updates local state only - the status will be saved when the user clicks "Save Template"
   */
  onStatusToggleChange(e: Event) {
    const target = e.target as any;
    const newStatus = target.checked ? "Active" : "Inactive";

    // Update local state only (will be saved when user clicks "Save Template")
    this.templateStatus = newStatus;
    this.markDirty();
    this.validateForm();
  }

  /**
   * Add a new variable row for inline editing
   */
  onAddVariableClick() {
    const newVariable: WorkflowTemplateVariable = {
      variableId: `new_${Date.now()}`, // UI-only: temp ID for tracking unsaved state
      name: "",
      description: "",
      regexRule: "",
      ruleDescription: "",
      isRequired: false,
      isEditing: true, // UI-only: indicates this row is in edit mode
    };

    this.variables = [...this.variables, newVariable];
    this.validateForm(); // Notify parent that a row is now being edited (tab needs attention)
    this.requestUpdate();
    const newIndex = this.variables.length - 1;
    void this.updateComplete.then(() => {
      this.focusVariableNameInput(newIndex);
    });
  }

  /**
   * Add a completed variable from external source (e.g., TemplateStringInput)
   * This is called by the parent component when a new variable is added from another tab
   * @param variableData - The variable data from the TemplateStringInput component (camelCase)
   * @returns boolean - true if variable was added, false if duplicate name
   */
  addVariableFromExternal(variableData: {
    name: string;
    description?: string;
    regexRule?: string;
    ruleDescription?: string;
  }): boolean {
    // TemplateStringInput dispatches camelCase properties
    // Trim all values, especially regexRule which may have accidental whitespace/tabs
    const varName = (variableData.name || "").trim();
    const varDescription = (variableData.description || "").trim();
    const varRegexRule = (variableData.regexRule || "").trim();
    const varRuleDescription = (variableData.ruleDescription || "").trim();

    if (isBuiltinRegionVariableName(varName)) {
      showToastMessage(
        "This name is reserved for the built-in Amazon S3 Remote region placeholder.",
        "danger",
      );
      return false;
    }

    if (isBuiltinBusUnitVariableName(varName)) {
      showToastMessage(
        "This name is reserved for the built-in Business Unit selector used at generate time.",
        "danger",
      );
      return false;
    }

    // Check for duplicate names
    const isDuplicate = this.variables.some(
      (v) => v.name.trim().toLowerCase() === varName.trim().toLowerCase(),
    );
    if (isDuplicate) {
      showToastMessage("A variable with this name already exists.", "danger");
      return false;
    }

    const newVariable: WorkflowTemplateVariable = {
      variableId: `new_${Date.now()}`, // UI-only: temp ID for tracking unsaved state
      name: varName,
      description: varDescription,
      regexRule: varRegexRule,
      ruleDescription: varRuleDescription,
      isRequired: false,
      isEditing: false, // UI-only: this variable is complete, not in edit mode
    };

    this.variables = [...this.variables, newVariable];
    this.markDirty();
    this.validateForm();
    this.requestUpdate();

    showToastMessage(
      `Variable {${varName}} added to Template Settings. Save the template to keep this variable.`,
      "success",
    );
    return true;
  }

  /**
   * Get the current variables array
   * Used by parent component to sync variables to other tabs
   */
  getVariables(): WorkflowTemplateVariable[] {
    return [...this.variables];
  }

  /**
   * Check if any variable is currently being edited
   * Used to disable Add/Edit buttons to enforce one-at-a-time editing
   */
  get isAnyVariableEditing(): boolean {
    return this.variables.some((v) => v.isEditing === true);
  }

  /**
   * Serialize variables for dirty detection (ignores UI-only flags)
   * Only includes API fields - array order is preserved
   */
  private serializeVariablesForDirty(
    variables: WorkflowTemplateVariable[],
  ): string {
    const normalized = variables
      .filter((v) => !v.isEditing) // Exclude unsaved rows
      .map((v) => ({
        name: v.name,
        description: v.description,
        regexRule: v.regexRule || "",
        ruleDescription: v.ruleDescription || "",
        isRequired: v.isRequired || false,
      }));
    return JSON.stringify(normalized);
  }

  /**
   * Track whether variables have unsaved changes
   */
  get hasUnsavedVariableChanges(): boolean {
    if (!this.originalData?.variablesJson) {
      return this.variables.some((v) => !v.isEditing);
    }
    return (
      this.serializeVariablesForDirty(this.variables) !==
      this.originalData.variablesJson
    );
  }

  /**
   * Focus the Variable Name input for the given row index
   */
  private focusVariableNameInput(index: number) {
    const input = this.shadowRoot?.querySelector(
      `input.inline-input[data-variable-index="${index}"]`,
    ) as HTMLInputElement | null;
    if (input) {
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }

  /**
   * Update variable field during inline editing
   */
  onVariableFieldChange(index: number, field: string, value: string) {
    const updatedVariables = [...this.variables];
    updatedVariables[index] = {
      ...updatedVariables[index],
      [field]: value,
    };
    this.variables = updatedVariables;

    // Real-time regex validation when regexRule field changes
    if (field === "regexRule") {
      this.validateRegexPattern(index, value);
    }
  }

  /**
   * Validate regex pattern syntax in real-time
   * @param index - Variable index
   * @param pattern - The regex pattern to validate
   */
  private validateRegexPattern(index: number, pattern: string): boolean {
    // Empty regex is valid (it's optional)
    if (!pattern || pattern.trim() === "") {
      this.regexErrors.delete(index);
      this.regexErrorMessages.delete(index);
      this.regexErrors = new Map(this.regexErrors);
      this.regexErrorMessages = new Map(this.regexErrorMessages);
      return true;
    }

    // Check for common regex mistakes before syntax check
    const commonMistake = this.detectCommonRegexMistakes(pattern);
    if (commonMistake) {
      this.regexErrors.set(index, true);
      this.regexErrorMessages.set(index, commonMistake);
      this.regexErrors = new Map(this.regexErrors);
      this.regexErrorMessages = new Map(this.regexErrorMessages);
      return false;
    }

    try {
      new RegExp(pattern);
      this.regexErrors.delete(index);
      this.regexErrorMessages.delete(index);
      this.regexErrors = new Map(this.regexErrors);
      this.regexErrorMessages = new Map(this.regexErrorMessages);
      return true;
    } catch (e) {
      this.regexErrors.set(index, true);
      this.regexErrorMessages.set(index, "Invalid regex syntax");
      this.regexErrors = new Map(this.regexErrors);
      this.regexErrorMessages = new Map(this.regexErrorMessages);
      return false;
    }
  }

  /**
   * Detect common regex mistakes that are syntactically valid but likely wrong
   *
   * Catches:
   * 1. Double dots for ranges: [a..z] should be [a-z]
   * 2. Reversed ranges: [z-a] should be [a-z]
   * 3. Missing brackets for character class: a-z should be [a-z]
   * 4. Spaces in character class: [a - z] should be [a-z]
   * 5. Using * alone (matches empty string): * should be .* or .+
   */
  private detectCommonRegexMistakes(pattern: string): string | null {
    // 1. Detect "a..z" or "A..Z" pattern (should be "a-z" or "A-Z")
    if (/[a-zA-Z]\.\.+[a-zA-Z]/.test(pattern)) {
      return "Use hyphen for ranges (e.g., a-z not a..z)";
    }

    // 2. Detect "[a..z]" style patterns inside brackets
    if (/\[[^\]]*\.\.[^\]]*\]/.test(pattern)) {
      return "Use hyphen for ranges (e.g., [a-z] not [a..z])";
    }

    // 3. Detect reversed ranges like [z-a] or [9-0]
    // Look for patterns like "z-a" or "Z-A" or "9-0" inside brackets
    // Use string matching since the regex itself would be invalid
    const reversedRangeMatch = pattern.match(/\[([^\]]*)\]/g);
    if (reversedRangeMatch) {
      for (const bracket of reversedRangeMatch) {
        const content = bracket.slice(1, -1); // Remove [ and ]
        // Check for reversed letter ranges (z-a, Z-A, etc.)
        const letterRangeMatch = content.match(/([a-zA-Z])-([a-zA-Z])/g);
        if (letterRangeMatch) {
          for (const range of letterRangeMatch) {
            const start = range.charCodeAt(0);
            const end = range.charCodeAt(2);
            if (start > end) {
              return "Range is reversed (e.g., use [a-z] not [z-a])";
            }
          }
        }
        // Check for reversed number ranges (9-0, etc.)
        const numberRangeMatch = content.match(/([0-9])-([0-9])/g);
        if (numberRangeMatch) {
          for (const range of numberRangeMatch) {
            const start = parseInt(range[0]);
            const end = parseInt(range[2]);
            if (start > end) {
              return "Range is reversed (e.g., use [0-9] not [9-0])";
            }
          }
        }
      }
    }

    // 4. Detect spaces around hyphens in character class: [a - z]
    if (/\[[^\]]*\s+-\s*[^\]]*\]|\[[^\]]*\s*-\s+[^\]]*\]/.test(pattern)) {
      return "Remove spaces around hyphen in range (e.g., [a-z] not [a - z])";
    }

    // 5. Detect standalone * without preceding character (common mistake)
    if (/^\*$/.test(pattern)) {
      return "Use .* for 'anything' or .+ for 'at least one character'";
    }

    return null;
  }

  /**
   * Check if a variable has a regex validation error
   */
  hasRegexError(index: number): boolean {
    return this.regexErrors.get(index) === true;
  }

  // Valid characters for variable names (a-zA-Z0-9_-)
  private static readonly VALID_VAR_NAME_CHARS = /^[a-zA-Z0-9_\-]$/;
  // Characters that cannot be at the start of a variable name
  private static readonly INVALID_START_CHARS = /^[-_]$/;

  /**
   * Handle keydown in Variable Name field - block invalid characters.
   * Allowed: a-zA-Z0-9_- but cannot start with - or _
   */
  onVarNameKeydown(e: KeyboardEvent) {
    // Allow control keys (backspace, delete, arrow keys, tab, etc.)
    if (
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "Tab" ||
      e.key === "Home" ||
      e.key === "End" ||
      e.key === "Enter" ||
      e.key === "Escape" ||
      e.ctrlKey ||
      e.metaKey
    ) {
      return; // Allow these keys
    }

    const input = e.target as HTMLInputElement;
    const currentValue = input?.value || "";
    const selectionStart = input?.selectionStart ?? currentValue.length;

    // If this would be the first character, don't allow - or _
    if (selectionStart === 0 && currentValue.length === 0) {
      if (WorkflowTemplateSettingsTab.INVALID_START_CHARS.test(e.key)) {
        e.preventDefault();
        return;
      }
    }

    // Block any character that's not in the valid set
    if (!WorkflowTemplateSettingsTab.VALID_VAR_NAME_CHARS.test(e.key)) {
      e.preventDefault();
    }
  }

  /**
   * Handle paste in Variable Name field - sanitize pasted content.
   * Removes invalid characters and ensures it doesn't start with - or _
   */
  onVarNamePaste(e: ClipboardEvent, index: number) {
    e.preventDefault();

    const pastedText = e.clipboardData?.getData("text") || "";
    const input = e.target as HTMLInputElement;
    const currentValue = input?.value || "";
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;

    // Sanitize pasted text: remove invalid chars
    let sanitized = pastedText.replace(/[^a-zA-Z0-9_\-]/g, "");

    // If pasting at the beginning, remove leading - or _
    if (selectionStart === 0) {
      sanitized = sanitized.replace(/^[-_]+/, "");
    }

    // Build new value
    const before = currentValue.slice(0, selectionStart);
    const after = currentValue.slice(selectionEnd);
    const newValue = before + sanitized + after;

    // Update the input value and state
    input.value = newValue;
    this.onVariableFieldChange(index, "name", newValue);

    // Set cursor position after pasted content
    const newCursorPos = selectionStart + sanitized.length;
    requestAnimationFrame(() => {
      input.setSelectionRange(newCursorPos, newCursorPos);
    });
  }

  /**
   * Handle input in Variable Name field - sanitize any invalid input
   */
  onVarNameInput(e: Event, index: number) {
    const input = e.target as HTMLInputElement;
    let value = input?.value || "";

    // Remove invalid characters and leading - or _
    const sanitized = value
      .replace(/[^a-zA-Z0-9_\-]/g, "")
      .replace(/^[\-_]+/, "");

    if (sanitized !== value) {
      const cursorPos = input.selectionStart ?? sanitized.length;
      input.value = sanitized;
      this.onVariableFieldChange(index, "name", sanitized);
      // Try to maintain cursor position
      const newPos = Math.min(cursorPos, sanitized.length);
      requestAnimationFrame(() => {
        input.setSelectionRange(newPos, newPos);
      });
    } else {
      this.onVariableFieldChange(index, "name", value);
    }
  }

  /**
   * Save inline edited variable
   */
  onSaveVariable(index: number) {
    const variable = this.variables[index];

    // Validate variable Name - allows a-zA-Z0-9_- but cannot start with - or _
    const nameRegex = /^[a-zA-Z0-9][a-zA-Z0-9_\-]*$/;
    if (!variable.name || variable.name.trim().length === 0) {
      showToastMessage("Variable Name is required.", "danger");
      return;
    }
    if (!nameRegex.test(variable.name)) {
      showToastMessage(
        "Variable Name must start with a letter or number and can only contain letters, numbers, underscores, and dashes.",
        "danger",
      );
      return;
    }
    if (isBuiltinRegionVariableName(variable.name)) {
      showToastMessage(
        "This name is reserved for the built-in Amazon S3 Remote region placeholder.",
        "danger",
      );
      return;
    }
    if (isBuiltinBusUnitVariableName(variable.name)) {
      showToastMessage(
        "This name is reserved for the built-in Business Unit selector used at generate time.",
        "danger",
      );
      return;
    }

    // Check for duplicate names
    const isDuplicate = this.variables.some(
      (v, i) =>
        i !== index &&
        v.name.trim().toLowerCase() === variable.name.trim().toLowerCase(),
    );
    if (isDuplicate) {
      showToastMessage("A variable with this name already exists.", "danger");
      return;
    }

    // Validate Description
    if (!variable.description || variable.description.trim().length === 0) {
      showToastMessage("Description is required.", "danger");
      return;
    }

    // Validate RegexRule if provided - check both syntax and common mistakes
    if (variable.regexRule && variable.regexRule.trim().length > 0) {
      // First check for common mistakes (syntactically valid but likely wrong)
      const commonMistake = this.detectCommonRegexMistakes(variable.regexRule);
      if (commonMistake) {
        // Don't save - user sees error message on the field
        showToastMessage(commonMistake, "danger");
        return;
      }

      // Then check for syntax errors
      try {
        new RegExp(variable.regexRule);
      } catch (e) {
        // Invalid regex - don't save. User sees orange border on the field.
        showToastMessage("Invalid regex syntax", "danger");
        return;
      }
    }

    // Clear regex error state for this variable on successful save
    this.regexErrors.delete(index);
    this.regexErrorMessages.delete(index);
    this.regexErrors = new Map(this.regexErrors);
    this.regexErrorMessages = new Map(this.regexErrorMessages);

    // Trim the regex pattern to remove any leading/trailing whitespace (including tabs)
    const updatedVariables = [...this.variables];
    updatedVariables[index] = {
      ...updatedVariables[index],
      regexRule: (updatedVariables[index].regexRule || "").trim(),
      isEditing: false,
    };
    this.variables = updatedVariables;

    showToastMessage(
      `Variable {${variable.name}} added to Template Settings. Save the template to keep this variable.`,
      "success",
    );
    this.markDirty();
    this.validateForm();
  }

  /**
   * Cancel inline variable editing
   */
  onCancelVariable(index: number) {
    const variable = this.variables[index];

    // Clear any regex validation error for this index
    this.regexErrors.delete(index);
    this.regexErrors = new Map(this.regexErrors);

    // If it's a new variable (never saved), remove it
    if (variable.variableId?.startsWith("new_")) {
      this.variables = this.variables.filter((_, i) => i !== index);
    } else {
      // If editing existing variable, just set edit flag to false
      const updatedVariables = [...this.variables];
      updatedVariables[index] = {
        ...updatedVariables[index],
        isEditing: false,
      };
      this.variables = updatedVariables;
    }
    this.validateForm(); // Notify parent that editing is complete (tab no longer needs attention)
    this.requestUpdate();
  }

  /**
   * Check if a variable is currently in use by template fields
   * Case-insensitive comparison since variable names may have different casing
   */
  isVariableInUse(variableName: string): boolean {
    const lowerName = variableName.toLowerCase();
    const inUse = this.variablesInUse.some(
      (v) => v.toLowerCase() === lowerName,
    );
    return inUse;
  }

  /**
   * Get tooltip text for delete button based on variable usage
   */
  getDeleteTooltip(variableName: string): string {
    if (this.isVariableInUse(variableName)) {
      return `Cannot delete - "${variableName}" is used in template fields`;
    }
    return "Delete variable";
  }

  /**
   * Delete a variable
   */
  onDeleteVariable(index: number) {
    const variable = this.variables[index];

    // Check if variable is in use
    if (variable && this.isVariableInUse(variable.name)) {
      showToastMessage(
        `Cannot delete "${variable.name}" - it is used in template fields. Remove all {${variable.name}} references first.`,
        "danger",
      );
      return;
    }

    // Clear any regex validation error for this index
    this.regexErrors.delete(index);
    this.regexErrors = new Map(this.regexErrors);

    this.variables = this.variables.filter((_, i) => i !== index);
    showToastMessage("Variable deleted successfully.", "success");
    this.markDirty();
    this.validateForm();
  }

  /**
   * Edit an existing variable
   */
  onEditVariable(index: number) {
    const updatedVariables = [...this.variables];
    updatedVariables[index] = {
      ...updatedVariables[index],
      isEditing: true,
    };
    this.variables = updatedVariables;
    this.validateForm(); // Notify parent that a row is now being edited (tab needs attention)
    this.requestUpdate();
  }

  /**
   * Validate entire form and emit validation state
   * Requires: Template Name, Description, Category, Business Unit (if applicable),
   * at least one saved variable, AND no variables currently being edited.
   *
   * When a variable is being edited (inline add/edit), the tab shows a warning icon
   * and the Save Template button is disabled until the user saves or cancels the row.
   */
  validateForm() {
    // Check if there's at least one saved variable (not currently being edited as new)
    const hasSavedVariables = this.variables.some(
      (v) => !v.isEditing || !v.variableId?.startsWith("new_"),
    );

    // Check if any variable is currently being edited (add or edit mode)
    // This is separate from validity - midEdit blocks save but doesn't mean data is invalid
    const midEdit = this.isAnyVariableEditing;

    // Basic validity: all required fields filled AND at least one saved variable
    const isValid =
      this.isTemplateNameValid &&
      this.isTemplateDescriptionValid &&
      this.isCategoryValid &&
      this.isBusinessUnitValid &&
      hasSavedVariables;

    this.emitValidation(isValid, midEdit);
  }

  /**
   * Emit validation, dirty, and midEdit state to parent
   * @param valid - Whether all required fields are filled
   * @param midEdit - Whether a variable row is currently being edited (blocks save)
   */
  emitValidation(valid: boolean, midEdit: boolean = false) {
    this.dispatchEvent(
      new CustomEvent("validation-change", {
        detail: {
          tabName: "settings",
          valid,
          dirty: this.isDirty,
          midEdit,
          variables: this.variables
            .filter((v) => !v.isEditing)
            .map((v) => ({ name: v.name })),
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Save current data as original values for dirty detection
   */
  saveOriginalData() {
    this.originalData = {
      templateName: this.templateName,
      templateDescription: this.templateDescription,
      visibilityLevel: this.visibilityLevel,
      businessUnit: this.businessUnit,
      templateStatus: this.templateStatus,
      variablesJson: this.serializeVariablesForDirty(this.variables),
    };
  }

  /**
   * Mark form as dirty (called when user makes changes)
   * Skips marking dirty during initial load to prevent false positives
   * from @change events firing when inputs are first populated
   */
  markDirty() {
    if (!this.isInitializing) {
      this.isDirty = true;
    }
  }

  /**
   * Get all form data for saving
   */
  getFormData() {
    // Filter out any variables still in edit mode and remove the isEditing flag
    const validVariables = this.variables
      .filter((v) => !v.isEditing)
      .map((v) => {
        const { isEditing, ...cleanVariable } = v as any;
        return cleanVariable;
      });

    return {
      templateName: this.templateName,
      templateDescription: this.templateDescription,
      visibilityLevel: this.visibilityLevel, // "Global" or "Business Unit"
      businessUnitId:
        this.visibilityLevel === "Business Unit" ? this.businessUnit : null, // businessunitid or null for Global
      status: this.templateStatus.toLowerCase(), // API expects lowercase: "active" or "inactive"
      variables: validVariables,
    };
  }

  /**
   * Render basic information section
   */
  renderBasicInfo() {
    const isViewMode = this.mode === "VIEW";
    const visibilityOptions = this.isBuAdminAddMode
      ? [{ value: "Business Unit", name: "Business Unit" }]
      : [
          { value: "Global", name: "Global" },
          { value: "Business Unit", name: "Business Unit" },
        ];

    const businessUnitOptionsForSelect =
      this.isBuAdminAddMode && this.loggedUserBu
        ? (() => {
            const match = (this.businessUnits ?? []).find(
              (b) => b.value.toLowerCase() === this.loggedUserBu?.toLowerCase(),
            );
            return match
              ? [match]
              : [{ value: this.loggedUserBu, name: this.loggedUserBu }];
          })()
        : (this.businessUnits ?? []);

    const categorySelectDisabled =
      isViewMode ||
      (this.mode === "ADD" && this.isBuAdminAddMode) ||
      (this.mode === "EDIT" && !this.isEnterpriseAdmin);

    const businessUnitSelectDisabled =
      isViewMode ||
      this.visibilityLevel === "Global" ||
      (this.isBusinessUnitAdmin &&
        !this.isEnterpriseAdmin &&
        this.visibilityLevel === "Business Unit");

    return html`
      <!-- Section Header with Title and Status Toggle -->
      <div id="sectionHeader">
        <h2>General Information</h2>

        <!-- Status Toggle (top right, aligned with h2) -->
        ${this.mode !== "VIEW"
          ? html`
              <div id="statusContainer">
                <label for="statusToggle">Template Active</label>
                <jha-tooltip
                  left
                  text="${this.templateStatus.toLowerCase() === " active"
                    ? "Click to set template to Inactive (will be saved when you click Save Template)"
                    : "Click to set template to Active (will be saved when you click Save Template)"}"
                >
                  <jha-form-switch
                    id="statusToggle"
                    data-auto-id="statusToggle"
                    ?checked=${this.templateStatus.toLowerCase() === "active"}
                    @change=${this.onStatusToggleChange}
                  >
                  </jha-form-switch>
                </jha-tooltip>
              </div>
            `
          : ""}
      </div>

      <div id="basicInfoContainer">
        <!-- Template Name -->
        <jha-tooltip
          bottom
          text="Use only letters, numbers, dots, dashes, or underscores. No spaces allowed. Max length 128 characters."
        >
          <jha-form-input
            label="Template Name"
            placeholder="Enter Template Name"
            required
            type="text"
            outline
            ?disabled=${isViewMode}
            data-auto-id="templateNameInput"
            .value=${this.templateName}
            @keydown=${this.onTemplateNameKeydown}
            @input=${this.onTemplateNameInput}
            @change=${(e: Event) =>
              this.validateTemplateName((e.target as any).value)}
            .maxlength=${WorkflowTemplateSettingsTab.TEMPLATE_NAME_MAX_LENGTH}
          >
          </jha-form-input>
        </jha-tooltip>

        <!-- Template Description -->
        <jha-tooltip
          bottom
          text="Provide a clear description of this template's purpose and usage. Max length 512 characters."
        >
          <jha-form-input
            label="Template Description"
            placeholder="Enter Template Description"
            required
            type="textarea"
            outline
            ?disabled=${isViewMode}
            data-auto-id="templateDescriptionInput"
            .value=${this.templateDescription}
            @change=${(e: Event) =>
              this.validateTemplateDescription((e.target as any).value)}
            .maxlength=${WorkflowTemplateSettingsTab.TEMPLATE_DESCRIPTION_MAX_LENGTH}
            characterLimit="512"
          >
          </jha-form-input>
        </jha-tooltip>

        <!-- Category -->
        <jha-tooltip
          bottom
          text=${this.isBuAdminAddMode
            ? "New templates are created for your business unit only."
            : "Select Global for all business units or Business Unit for specific access."}
        >
          <jha-form-select
            outline
            label="Category"
            .options=${visibilityOptions}
            lableKey="name"
            valueKey="value"
            required
            warning="Category is required"
            ?disabled=${categorySelectDisabled}
            data-auto-id="categorySelect"
            .value=${this.visibilityLevel}
            @change=${this.onCategoryChange}
            emptyOptionLabel="Select Category"
          >
          </jha-form-select>
        </jha-tooltip>

        <!-- Business Unit (always shown, required only when Category is Business Unit) -->
        <jha-tooltip
          bottom
          text=${this.isBuAdminAddMode
            ? "Template will be available only to this business unit."
            : "Select the business unit that will have access to this template."}
        >
          <jha-form-select
            outline
            label="Business Unit"
            .options=${businessUnitOptionsForSelect}
            lableKey="name"
            valueKey="value"
            ?required=${this.visibilityLevel === "Business Unit"}
            warning="Business Unit is required when Category is Business Unit"
            ?disabled=${businessUnitSelectDisabled}
            data-auto-id="businessUnitSelect"
            .value=${this.businessUnit}
            @change=${this.onBusinessUnitChange}
            emptyOptionLabel="Select Business Unit"
          >
          </jha-form-select>
        </jha-tooltip>
      </div>
    `;
  }

  /**
   * Render variables table with inline editing
   */
  renderVariablesTable() {
    const canEditVariables = this.mode !== "VIEW";

    return html`
      <jha-table>
        <jha-table-row>
          <jha-table-header style="width: 60px;">NO.</jha-table-header>
          <jha-table-header style="width: 200px;">NAME</jha-table-header>
          <jha-table-header style="min-width: 250px;"
            >DESCRIPTION</jha-table-header
          >
          <jha-table-header style="width: 180px;">RULE</jha-table-header>
          <jha-table-header style="width: 200px;"
            >RULE DESCRIPTION</jha-table-header
          >
          ${canEditVariables
            ? html`<jha-table-header style="width: 80px; text-align: center;"
                >ACTIONS</jha-table-header
              >`
            : ""}
        </jha-table-row>
        ${this.variables.map(
          (variable, index) => html`
            <jha-table-row>
              <jha-table-cell>${index + 1}</jha-table-cell>

              <!-- Variable Name -->
              <jha-table-cell>
                ${variable.isEditing
                  ? html`
                      <input
                        type="text"
                        class="inline-input ${!variable.variableId?.startsWith(
                          "new_",
                        )
                          ? "readonly"
                          : ""}"
                        data-variable-index="${index}"
                        data-auto-id="variableNameInput"
                        .value=${variable.name}
                        @keydown=${(e: KeyboardEvent) =>
                          this.onVarNameKeydown(e)}
                        @paste=${(e: ClipboardEvent) =>
                          this.onVarNamePaste(e, index)}
                        @input=${(e: Event) => this.onVarNameInput(e, index)}
                        placeholder="Variable Name"
                        maxlength="64"
                        ?disabled=${!variable.variableId?.startsWith("new_")}
                        title=${!variable.variableId?.startsWith("new_")
                          ? "Variable name cannot be changed after creation. Delete and re-add to change the name."
                          : ""}
                      />
                    `
                  : html`{${variable.name}}`}
              </jha-table-cell>

              <!-- Description -->
              <jha-table-cell>
                ${variable.isEditing
                  ? html`
                      <input
                        type="text"
                        class="inline-input"
                        data-auto-id="variableDescriptionInput"
                        .value=${variable.description}
                        @input=${(e: any) =>
                          this.onVariableFieldChange(
                            index,
                            "description",
                            e.target.value,
                          )}
                        placeholder="Description"
                        maxlength="256"
                      />
                    `
                  : html`${variable.description}`}
              </jha-table-cell>

              <!-- RegexRule -->
              <jha-table-cell>
                ${variable.isEditing
                  ? html`
                      <input
                        type="text"
                        class="inline-input ${this.hasRegexError(index)
                          ? " invalid"
                          : ""}"
                        data-auto-id="variableRegexRuleInput"
                        .value=${variable.regexRule || ""}
                        @input=${(e: any) =>
                          this.onVariableFieldChange(
                            index,
                            "regexRule",
                            e.target.value,
                          )}
                        placeholder="Regex (optional)"
                        title="${this.hasRegexError(index)
                          ? this.regexErrorMessages.get(index) ||
                            "Invalid regex pattern"
                          : ""}"
                        maxlength="256"
                      />
                      ${this.hasRegexError(index)
                        ? html`<span class="regex-error-message"
                            >${this.regexErrorMessages.get(index) ||
                            "Invalid regex pattern"}</span
                          >`
                        : ""}
                    `
                  : html`${variable.regexRule || ""}`}
              </jha-table-cell>

              <!-- RuleDescription -->
              <jha-table-cell>
                ${variable.isEditing
                  ? html`
                      <input
                        type="text"
                        class="inline-input"
                        data-auto-id="variableRuleDescriptionInput"
                        .value=${variable.ruleDescription || ""}
                        @input=${(e: any) =>
                          this.onVariableFieldChange(
                            index,
                            "ruleDescription",
                            e.target.value,
                          )}
                        placeholder="Rule Description (optional)"
                        maxlength="256"
                      />
                    `
                  : html`${variable.ruleDescription || ""}`}
              </jha-table-cell>

              <!-- Actions -->
              ${canEditVariables
                ? html`
                    <jha-table-cell class="action-cell">
                      <div class="action-icons">
                        ${variable.isEditing
                          ? html`
                              <jha-tooltip top text="Save Variable">
                                <jha-icon-circle-checkmark-filled
                                  class="action-icon"
                                  data-auto-id="variableSaveIcon"
                                  @click=${() => this.onSaveVariable(index)}
                                >
                                </jha-icon-circle-checkmark-filled>
                              </jha-tooltip>
                              <jha-tooltip top text="Cancel">
                                <jha-icon-close
                                  class="action-icon"
                                  data-auto-id="variableCancelIcon"
                                  @click=${() => this.onCancelVariable(index)}
                                >
                                </jha-icon-close>
                              </jha-tooltip>
                            `
                          : html`
                              <jha-tooltip
                                left
                                text=${this.isAnyVariableEditing
                                  ? "Save or cancel the current variable first"
                                  : "Edit Variable"}
                              >
                                <jha-icon-edit
                                  class="action-icon ${this.isAnyVariableEditing
                                    ? " disabled"
                                    : ""}"
                                  data-auto-id="variableEditIcon"
                                  @click=${() =>
                                    !this.isAnyVariableEditing &&
                                    this.onEditVariable(index)}
                                >
                                </jha-icon-edit>
                              </jha-tooltip>
                              <jha-tooltip
                                left
                                text=${this.isAnyVariableEditing
                                  ? "Save or cancel the current variable first"
                                  : this.getDeleteTooltip(variable.name)}
                              >
                                <jha-icon-delete
                                  class="action-icon ${this
                                    .isAnyVariableEditing ||
                                  this.isVariableInUse(variable.name)
                                    ? " disabled"
                                    : ""}"
                                  data-auto-id="variableDeleteIcon"
                                  @click=${() =>
                                    !this.isAnyVariableEditing &&
                                    this.onDeleteVariable(index)}
                                >
                                </jha-icon-delete>
                              </jha-tooltip>
                            `}
                      </div>
                    </jha-table-cell>
                  `
                : ""}
            </jha-table-row>
          `,
        )}
      </jha-table>
    `;
  }

  render() {
    // Show loading indicator while data is being fetched
    if (this.apiLoading) {
      return html`
        <div
          style="display: flex; justify-content: center; align-items: center; min-height: 200px;"
        >
          <circle-loader></circle-loader>
        </div>
      `;
    }

    const canEditVariables = this.mode !== "VIEW";

    return html`
      <div>
        <!-- General Information Section -->
        ${this.renderBasicInfo()}

        <!-- Variables Section -->
        <div id="variablesSection">
          <div class="variables-section-heading">
            <h2>Variables</h2>
            ${this.mode !== "VIEW"
              ? html`<span
                  class="variables-section-hint"
                  data-auto-id="variablesRequiredHint"
                  >At least one variable is required.</span
                >`
              : ""}
          </div>
          ${this.hasUnsavedVariableChanges
            ? html`<div class="variables-warning">
                <jha-icon-warning-filled></jha-icon-warning-filled>
                Variables may have been changed. Save the template to ensure the
                variable(s) and template are in sync.
                <jha-icon-warning-filled></jha-icon-warning-filled>
              </div>`
            : ""}

          <!-- Variables Table (conditionally rendered) -->
          ${this.variables.length > 0
            ? html`
                <div id="variablesTableContainer">
                  ${this.renderVariablesTable()}
                </div>
              `
            : html`
                <!-- Show table header and "No Variables" message (like BusinessUnits) -->
                <jha-table>
                  <jha-table-row>
                    <jha-table-header style="width: 60px;"
                      >NO.</jha-table-header
                    >
                    <jha-table-header style="width: 200px;"
                      >NAME</jha-table-header
                    >
                    <jha-table-header style="min-width: 250px;"
                      >DESCRIPTION</jha-table-header
                    >
                    <jha-table-header style="width: 180px;"
                      >RULE</jha-table-header
                    >
                    <jha-table-header style="width: 200px;"
                      >RULE DESCRIPTION</jha-table-header
                    >
                    ${canEditVariables
                      ? html`<jha-table-header
                          style="width: 80px; text-align: center;"
                          >ACTIONS</jha-table-header
                        >`
                      : ""}
                  </jha-table-row>
                </jha-table>
                <div class="no-variables-message">
                  <h3>No Variables Found</h3>
                </div>
              `}

          <!-- Add Variable Button (only in edit mode) -->
          ${canEditVariables
            ? html`
                <div id="addVariableButtonContainer">
                  <jha-tooltip
                    left
                    text=${this.isAnyVariableEditing
                      ? "Save or cancel the current variable first"
                      : "Click to add a new variable"}
                  >
                    <jha-button
                      type="button"
                      primary
                      sync
                      ?disabled=${this.apiLoading || this.isAnyVariableEditing}
                      data-auto-id="addVariableBtn"
                      @click=${this.onAddVariableClick}
                    >
                      <jha-icon-plus slot="icon-left"></jha-icon-plus>
                      Add Variable
                    </jha-button>
                  </jha-tooltip>
                </div>
              `
            : ""}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "workflow-template-settings-tab": WorkflowTemplateSettingsTab;
  }
}
